/**
 * Utility functions for Student Profile Component
 */

/**
 * Get initials from a name
 * @param {string} name - Full name
 * @returns {string} - Initials
 */
export const getInitials = (name) => {
    if (!name) return '';
    const names = name.trim().split(' ');
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return names[0][0].toUpperCase() + names[names.length - 1][0].toUpperCase();
}

/**
 * Get month number from month name
 * @param {string} monthName - Month name (Jan, Feb, etc.)
 * @returns {number} - Month number (1-12)
 */
export const getMonthNumber = (monthName) => {
    const months = {
        'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
        'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
    };
    return months[monthName] || 4;
}

/**
 * Format date from string
 * @param {string} dateString - Date string in format "2025-08-01"
 * @returns {string} - Formatted date "01 Aug 2025"
 */
export const formatDate = (dateString) => {
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    } catch (error) {
        return dateString; // Return original if formatting fails
    }
}

/**
 * Normalize status from API to match expected values
 * @param {string} status - Status from API
 * @returns {string} - Normalized status
 */
export const normalizeStatus = (status) => {
    // Normalize status names from API to match our expected values
    const statusMap = {
        'Present': 'Present',
        'Absent': 'Absent',
        'WeekOff': 'Weekoff',
        'NoData': 'NoData',
        'Holiday': 'Holiday',
        'Late': 'Late'
    };

    return statusMap[status] || status; // Return mapped status or original if not found
}

/**
 * Calculate totals from months array
 * @param {Array} monthsArray - Array of month data objects
 * @returns {Object} - Totals object
 */
export const calculateTotalsFromMonths = (monthsArray) => {
    if (!monthsArray || monthsArray.length === 0) {
        return {
            noOfDays: 0,
            weekoffs: 0,
            holidays: 0,
            workingDays: 0,
            present: 0,
            absent: 0,
            notMarked: 0,
            late: 0,
            halfday: 0,
            percentage: 0
        };
    }

    const totals = monthsArray.reduce((acc, month) => {
        return {
            noOfDays: (acc.noOfDays || 0) + (parseInt(month.noOfDays) || 0),
            weekoffs: (acc.weekoffs || 0) + (parseInt(month.weekoffs) || 0),
            holidays: (acc.holidays || 0) + (parseInt(month.holidays) || 0),
            workingDays: (acc.workingDays || 0) + (parseInt(month.workingDays) || 0),
            present: (acc.present || 0) + (parseInt(month.present) || 0),
            absent: (acc.absent || 0) + (parseInt(month.absent) || 0),
            notMarked: (acc.notMarked || 0) + (parseInt(month.notMarked) || 0),
            late: (acc.late || 0) + (parseInt(month.late) || 0),
            halfday: (acc.halfday || 0) + (parseInt(month.halfday) || 0)
        };
    }, {});

    // Calculate percentage
    if (totals.workingDays > 0) {
        totals.percentage = Math.round((totals.present / totals.workingDays) * 100 * 100) / 100;
    } else {
        totals.percentage = 0;
    }

    return totals;
}

/**
 * Generate daily attendance data for a month
 * @param {string} month - Month string "Apr 2025"
 * @param {string} attendanceType - Type of attendance
 * @returns {Array} - Array of daily attendance objects
 */
export const generateDailyAttendanceData = (month, attendanceType) => {
    // Generate sample daily attendance data based on the month
    const dailyData = [];
    const monthName = month ? month.split(' ')[0] : 'Apr';
    const year = month ? month.split(' ')[1] : '2025';

    // Get days in month
    const daysInMonth = new Date(parseInt(year), getMonthNumber(monthName), 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(parseInt(year), getMonthNumber(monthName) - 1, day);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        const dateStr = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

        let status = 'Present';

        // Weekend logic
        if (dayName === 'Sunday') {
            status = 'Weekoff';
        }
        // Add some holiday dates (example: 19th and 20th of October for Diwali season)
        else if ((monthName === 'Oct' && (day === 19 || day === 20)) ||
            (monthName === 'Dec' && day === 25) ||
            (monthName === 'Jan' && day === 1)) {
            status = 'Holiday';
        } else {
            // Random attendance pattern for demo
            const rand = Math.random();
            if (rand < 0.8) status = 'Present';
            else if (rand < 0.9) status = 'Absent';
            else status = 'Late';
        }

        dailyData.push({
            date: dateStr,
            day: dayName,
            status: status
        });
    }

    return dailyData;
}

/**
 * Group fee data by feeTypeId and calculate sums
 * @param {Array} feeData - Array of fee data from API (agg array)
 * @returns {Object} - Object with grouped data and totals
 */
export const groupAndSumFeesByType = (feeData) => {
    if (!feeData || !Array.isArray(feeData) || feeData.length === 0) {
        return {
            regularFees: [],
            miscellaneousFees: [],
            regularTotal: { feeAmount: 0, concession: 0, netAmount: 0, paidAmount: 0, balance: 0 },
            miscellaneousTotal: { feeAmount: 0, concession: 0, netAmount: 0, paidAmount: 0, balance: 0 },
            grandTotal: { feeAmount: 0, concession: 0, netAmount: 0, paidAmount: 0, balance: 0 }
        };
    }

    // Group by feeTypeId
    const groupedData = {};
    
    feeData.forEach(item => {
        const feeTypeId = item.feeTypeId;
        
        if (!groupedData[feeTypeId]) {
            groupedData[feeTypeId] = {
                feeType: item.feeType,
                feeTypeId: feeTypeId,
                planName: item.planName || '',
                categoryName: item.categoryName || '',
                feeStructureName: item.feeStructureName || '',
                priority: item.priority || 0,
                termWiseActualValue: 0,
                refundAmt: 0,
                dueAmount: 0,
                concession: 0,
                amountPaid: 0,
                isPlanExists: item.isPlanExists || false,
                isUnassignApprovalRaised: item.isUnassignApprovalRaised || false
            };
        }
        
        // Sum the values
        groupedData[feeTypeId].termWiseActualValue += item.termWiseActualValue || 0;
        groupedData[feeTypeId].refundAmt += item.refundAmt || 0;
        groupedData[feeTypeId].dueAmount += item.dueAmount || 0;
        groupedData[feeTypeId].concession += item.concession || 0;
        groupedData[feeTypeId].amountPaid += item.amountPaid || 0;
    });

    // Convert to array and sort by priority
    const allFees = Object.values(groupedData).sort((a, b) => a.priority - b.priority);

    // Separate regular and miscellaneous fees based on isPlanExists
    // If isPlanExists is true, it goes to Regular Fees, else Miscellaneous Fees
    const regularFees = [];
    const miscellaneousFees = [];

    allFees.forEach(fee => {
        // Get payment term from original data for this fee type
        const originalItems = feeData.filter(item => item.feeTypeId === fee.feeTypeId);
        const paymentTerm = originalItems.length > 0 ? originalItems[0].paymentTerm : '';
        
        // Add paymentTerm to fee object for later use
        fee.paymentTerm = paymentTerm;
        
        if (fee.isPlanExists) {
            regularFees.push(fee);
        } else {
            miscellaneousFees.push(fee);
        }
    });

    // Calculate totals
    const calculateTotal = (fees) => {
        return fees.reduce((acc, fee) => {
            acc.feeAmount += fee.termWiseActualValue;
            acc.concession += fee.concession;
            acc.netAmount += (fee.termWiseActualValue - fee.concession );
            acc.paidAmount += fee.amountPaid;
            acc.balance += fee.dueAmount;
            return acc;
        }, { feeAmount: 0, concession: 0, netAmount: 0, paidAmount: 0, balance: 0 });
    };

    const regularTotal = calculateTotal(regularFees);
    const miscellaneousTotal = calculateTotal(miscellaneousFees);
    
    const grandTotal = {
        feeAmount: regularTotal.feeAmount + miscellaneousTotal.feeAmount,
        concession: regularTotal.concession + miscellaneousTotal.concession,
        netAmount: regularTotal.netAmount + miscellaneousTotal.netAmount,
        paidAmount: regularTotal.paidAmount + miscellaneousTotal.paidAmount,
        balance: regularTotal.balance + miscellaneousTotal.balance
    };

    return {
        regularFees,
        miscellaneousFees,
        regularTotal,
        miscellaneousTotal,
        grandTotal
    };
}
